import React from 'react';
import styled from 'styled-components';
import { Card, H2, ModalFactory, ModalLayout, Text } from 'components';
import { theme, toRem } from 'design';
import { IPOSProvider } from 'types';
import { formatDateForUILongFormat, getTimeWithoutZone } from 'utils/date';

const ModalContent = styled(ModalLayout)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-width: ${toRem(265)};

  @media (max-width: ${theme.breakpoint.xsmall}px) {
    padding: ${toRem(28)};
  }
`;

const Timecard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 0 ${toRem(18)};
  margin-top: ${toRem(28)};
  background: ${theme.color.white};
  overflow: visible;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: auto;
`;

const TopRow = styled(Row)`
  display: flex;
  flex-flow: row wrap;
  gap: ${toRem(6)};
  padding: ${toRem(20)};
  margin-bottom: ${toRem(20)};
  border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};

  span {
    font-size: ${toRem(15)};

    @media (max-width: ${theme.breakpoint.xsmall}px) {
      font-size: ${toRem(13)};
    }
  }
`;

const JobSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const JobRow = styled(Row)`
  padding: ${toRem(6)} ${toRem(22)};

  p,
  span {
    min-width: ${toRem(95)};
  }

  @media (max-width: ${theme.breakpoint.xsmall}px) {
    p,
    span {
      min-width: ${toRem(60)};
      font-size: ${toRem(12)};
    }
  }
`;

const BreakRow = styled(Row)`
  padding: ${toRem(6)} ${toRem(22)};

  p,
  span {
    color: ${theme.color.neutral[500]};
    min-width: ${toRem(95)};
  }

  @media (max-width: ${theme.breakpoint.xsmall}px) {
    p,
    span {
      min-width: ${toRem(60)};
      font-size: ${toRem(12)};
    }
  }
`;

const JobRowHeader = styled.span`
  font-size: ${toRem(14)};
  font-weight: ${theme.font.weight.medium};
  color: ${theme.color.neutral[500]};
`;

const TimeSection = styled.div`
  display: flex;
  flex-direction: row;
  width: 45%;

  @media (max-width: ${theme.breakpoint.small}px) {
    width: auto;
  }
`;

const ModalTitle = styled(H2)`
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.semiBold};
  margin-bottom: ${toRem(12)};
`;

const Timezone = styled(Text)`
  font-size: ${toRem(15)};
  color: ${theme.color.neutral[500]};
  margin-top: ${toRem(6)};

  @media (max-width: ${theme.breakpoint.xsmall}px) {
    font-size: ${toRem(13)};
  }
`;

const PayeeName = styled.span`
  font-weight: ${theme.font.weight.bold};
  color: ${theme.color.neutral[900]};
`;

const PayeeId = styled.span`
  color: ${theme.color.neutral[500]};
  text-transform: uppercase;
  word-break: break-all;
`;

interface ITimecardModalProps {
  timecardData: IPOSProvider;
  paymentDate: string;
  timezone: string;
  hideModal: () => void;
}

export const TimecardModal = ({ timecardData, paymentDate, timezone, hideModal }: ITimecardModalProps) => (
  <ModalFactory customModal onClose={hideModal} isActive>
    <ModalContent maxWidth={500} onClose={hideModal} isActive data-cy="timecard-modal">
      <ModalTitle>Timecard Data</ModalTitle>
      <Text>For the business day of {formatDateForUILongFormat(paymentDate)}</Text>
      <Timezone data-cy="timezone">{timezone?.replaceAll('_', ' ')}</Timezone>
      <Timecard>
        <TopRow>
          <PayeeName>
            {timecardData.firstName} {timecardData.lastName}
          </PayeeName>
          {timecardData.customerEmployeeId && <PayeeId>ID: {timecardData.customerEmployeeId}</PayeeId>}
        </TopRow>

        <JobRow>
          <JobRowHeader>Job Title</JobRowHeader>
          <TimeSection>
            <JobRowHeader>Clock In</JobRowHeader>
            <JobRowHeader>Clock Out</JobRowHeader>
          </TimeSection>
        </JobRow>

        {timecardData.schedules.map((timecard, i) => (
          <JobSection key={`${timecard.jobTitle}-${i}`}>
            <JobRow>
              <Text size="small">{timecard.jobTitle}</Text>
              <TimeSection>
                <Text size="small">{getTimeWithoutZone(timecard.start, timezone)}</Text>
                <Text size="small">{getTimeWithoutZone(timecard.end, timezone)}</Text>
              </TimeSection>
            </JobRow>
            {timecard.breaks.map((br, idx) => (
              <BreakRow key={`${timecard.jobTitle}-${i}-break-${idx}`}>
                <Text size="small">Break</Text>
                <TimeSection>
                  <Text size="small">{getTimeWithoutZone(br.start, timezone)}</Text>
                  <Text size="small">{getTimeWithoutZone(br.end, timezone)}</Text>
                </TimeSection>
              </BreakRow>
            ))}
          </JobSection>
        ))}
      </Timecard>
    </ModalContent>
  </ModalFactory>
);
